<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faChartLine,
    faExclamationTriangle,
    faCircleCheck,
    faCircleExclamation
  } from '@fortawesome/pro-light-svg-icons';

  export let stats = [];
  export let studyProperties = null;

  // Get the oldest sensor data with at least 1000 readings
  $: baselineStat = stats.length > 0 
    ? stats.find(stat => stat.total >= 1000) 
    : null;

  // Helper function to determine if CV is in good range (<36%)
  function isGoodCV(cv) {
    return cv < 36;
  }

  // Helper function to determine if TIR is in good range (>70%)
  function isGoodTIR(tir) {
    return tir > 70;
  }

  // Helper function to determine if TBR is in good range (<4%)
  function isGoodTBR(tbr) {
    return tbr < 4;
  }

  // Helper function to determine if TAR is in good range (<25%)
  function isGoodTAR(tar) {
    return tar < 25;
  }

  // Helper function to determine if events are in good range
  function isGoodEvents(hypo, hyper) {
    return hypo === 0 && hyper === 0;
  }
</script>

{#if baselineStat}
  <div class="flex">
    <!-- <div class="flex items-center justify-center w-6">
      <span class="text-sm font-semibold text-gray-500 dark:text-gray-400 transform -rotate-90 whitespace-nowrap">Baseline</span>
    </div> -->
    <div class="grid grid-cols-5 gap-2 px-1 mt-1 flex-1">
      <!-- CV% Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodCV(baselineStat.cvPercent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodCV(baselineStat.cvPercent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodCV(baselineStat.cvPercent) ? 'text-green-500' : 'text-yellow-500'}"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Baseline CV%</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{baselineStat.cvPercent}%</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Target: &lt;36%</p>
      </div>

      <!-- TBR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTBR(baselineStat.veryLow.percent + baselineStat.low.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTBR(baselineStat.veryLow.percent + baselineStat.low.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTBR(baselineStat.veryLow.percent + baselineStat.low.percent) ? 'text-green-500' : 'text-yellow-500'}"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Baseline TBR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(baselineStat.veryLow.percent + baselineStat.low.percent).toFixed(1)}%</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Time Below Range</p>
      </div>

      <!-- TIR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTIR(baselineStat.target.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTIR(baselineStat.target.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTIR(baselineStat.target.percent) ? 'text-green-500' : 'text-yellow-500'}"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Baseline TIR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{baselineStat.target.percent}%</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Time in Range</p>
      </div>

      <!-- TAR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTAR(baselineStat.high.percent + baselineStat.veryHigh.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTAR(baselineStat.high.percent + baselineStat.veryHigh.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTAR(baselineStat.high.percent + baselineStat.veryHigh.percent) ? 'text-green-500' : 'text-yellow-500'}"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Baseline TAR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(baselineStat.high.percent + baselineStat.veryHigh.percent).toFixed(1)}%</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Time Above Range</p>
      </div>

      <!-- Events Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodEvents(baselineStat.hypoEvents, baselineStat.hyperEvents) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            class="text-xl text-yellow-500"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Baseline Events</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">
          {baselineStat.hypoEvents} Hypo / {baselineStat.hyperEvents} Hyper
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Events >{(studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} min</p>
      </div>
    </div>
  </div>
{/if} 